import {DOCUMENT} from '@angular/common';
import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild, inject} from '@angular/core';
import {SmContextMenuItemModel, type SmContextMenuSelectionStyle} from '../sm-context-menu-item/sm-context-menu-item.component';
import {SmModalStackService} from '../sm-confirmation-dialog/sm-modal-stack.service';

export type SmContextMenuTriggerVariant = 'plain' | 'ellipsis';

@Component({
  selector: 'sm-context-menu',
  templateUrl: './sm-context-menu.component.html',
  styleUrls: ['./sm-context-menu.component.scss'],
  standalone: false,
})
export class SmContextMenuComponent implements OnChanges, OnDestroy {
  private readonly stretchMinWidth = 208;
  private static activeMenu: SmContextMenuComponent | null = null;
  private readonly document = inject(DOCUMENT);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly modalStack = inject(SmModalStackService);
  private positionTimer: number | null = null;
  private anchorPoint: {x: number; y: number} | null = null;
  private panelPortalHost: HTMLElement | null = null;

  @Input() items: SmContextMenuItemModel[] = [];
  @Input() isOpen = false;
  @Input() menuWidth: 'content' | 'stretch' = 'content';
  @Input() triggerAriaLabel = '';
  @Input() triggerTooltip: string | null = null;
  @Input() triggerTooltipAnchor: string | null = null;
  @Input() triggerTooltipBlurOnEscape = true;
  @Input() triggerVariant: SmContextMenuTriggerVariant = 'plain';
  @Input() selectionStyle: SmContextMenuSelectionStyle = 'none';

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() itemSelect = new EventEmitter<SmContextMenuItemModel>();

  @ViewChild('triggerButton', {read: ElementRef}) private readonly triggerButtonRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('panel', {read: ElementRef}) private readonly panelRef?: ElementRef<HTMLDivElement>;

  panelTop = 0;
  panelLeft = 0;
  panelWidth: number | null = null;
  panelReady = false;

  toggle(): void {
    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  open(): void {
    const previousMenu = SmContextMenuComponent.activeMenu;
    if (previousMenu && previousMenu !== this) {
      previousMenu.close();
    }

    SmContextMenuComponent.activeMenu = this;
    this.anchorPoint = this.getTriggerAnchorPoint();
    this.setOpenState(true);
    this.setInitialPanelPosition();
    this.schedulePanelPosition();
  }

  close(): void {
    this.modalStack.closedOverlay(this);
    this.detachPanel();
    this.setOpenState(false);
    this.panelReady = false;
    this.panelWidth = null;
    this.anchorPoint = null;

    if (SmContextMenuComponent.activeMenu === this) {
      SmContextMenuComponent.activeMenu = null;
    }
  }

  selectItem(item: SmContextMenuItemModel): void {
    if (item.disabled) {
      return;
    }

    this.close();
    // The selected item is detached; keep a stable focus origin for dialogs.
    this.triggerButtonRef?.nativeElement.focus({preventScroll: true});
    this.itemSelect.emit(item);
  }

  trackById(_: number, item: SmContextMenuItemModel): string {
    return item.id;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.modalStack.openedOverlay(this, () => this.close());
        this.setInitialPanelPosition();
        this.schedulePanelPosition();
      } else {
        this.modalStack.closedOverlay(this);
        this.panelReady = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.modalStack.closedOverlay(this);
    if (SmContextMenuComponent.activeMenu === this) {
      SmContextMenuComponent.activeMenu = null;
    }

    this.detachPanel();

    if (this.positionTimer !== null) {
      window.clearTimeout(this.positionTimer);
      this.positionTimer = null;
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event): void {
    const target = event.target as Node | null;
    const panel = this.panelRef?.nativeElement;

    if (!target) {
      return;
    }

    if (!this.elementRef.nativeElement.contains(target) && !(panel?.contains(target) ?? false)) {
      this.close();
    }
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  @HostListener('window:scroll')
  handleWindowScroll(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  private setOpenState(nextValue: boolean): void {
    if (this.positionTimer !== null) {
      window.clearTimeout(this.positionTimer);
      this.positionTimer = null;
    }

    this.isOpen = nextValue;
    if (nextValue) this.modalStack.openedOverlay(this, () => this.close());
    else this.modalStack.closedOverlay(this);
    this.isOpenChange.emit(nextValue);
  }

  private schedulePanelPosition(): void {
    if (this.positionTimer !== null) {
      window.clearTimeout(this.positionTimer);
    }

    this.positionTimer = window.setTimeout(() => {
      this.positionTimer = null;
      this.positionPanel();
    }, 0);
  }

  private setInitialPanelPosition(): void {
    const anchor = this.anchorPoint ?? this.getTriggerAnchorPoint();
    const fallbackLeft = anchor?.x ?? 0;
    const fallbackTop = anchor?.y ?? 0;
    const viewportMargin = 8;
    const gap = 6;

    this.panelLeft = this.clamp(fallbackLeft, viewportMargin, window.innerWidth - viewportMargin);
    this.panelTop = this.clamp(fallbackTop + gap, viewportMargin, window.innerHeight - viewportMargin);
    this.panelWidth = this.menuWidth === 'stretch'
      ? this.getStretchPanelWidth()
      : null;
    this.panelReady = false;
  }

  private positionPanel(): void {
    const triggerButton = this.triggerButtonRef?.nativeElement;
    const panel = this.panelRef?.nativeElement;

    if (!triggerButton || !panel) {
      return;
    }

    this.attachPanel(panel);

    const triggerRect = triggerButton.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportMargin = 8;
    const gap = 6;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = triggerRect.right - panelRect.width;
    let top = triggerRect.bottom + gap;

    if (left + panelRect.width > viewportWidth - viewportMargin) {
      left = viewportWidth - panelRect.width - viewportMargin;
    }

    if (left < viewportMargin) {
      left = viewportMargin;
    }

    if (top + panelRect.height > viewportHeight - viewportMargin) {
      top = triggerRect.top - panelRect.height - gap;
    }

    if (top < viewportMargin) {
      top = viewportMargin;
    }

    this.panelLeft = left;
    this.panelTop = top;
    this.panelWidth = this.menuWidth === 'stretch'
      ? this.getStretchPanelWidth(triggerRect.width)
      : null;
    this.panelReady = true;
  }

  private attachPanel(panel: HTMLElement): void {
    const element = this.elementRef.nativeElement as HTMLElement;
    const host = element.closest<HTMLElement>('dialog[data-sm-modal][open]')
      ?? this.document.body;
    if (panel.parentElement !== host) this.renderer.appendChild(host, panel);
    this.panelPortalHost = host;
  }

  private detachPanel(): void {
    const panel = this.panelRef?.nativeElement;
    if (panel && this.panelPortalHost && panel.parentElement === this.panelPortalHost)
      this.renderer.removeChild(this.panelPortalHost, panel);
    this.panelPortalHost = null;
  }

  private getTriggerAnchorPoint(): {x: number; y: number} | null {
    const triggerButton = this.triggerButtonRef?.nativeElement;
    if (!triggerButton) {
      return null;
    }

    const triggerRect = triggerButton.getBoundingClientRect();
    return {
      x: triggerRect.right,
      y: triggerRect.bottom,
    };
  }

  private getTriggerWidth(): number | null {
    return this.triggerButtonRef?.nativeElement.getBoundingClientRect().width ?? null;
  }

  private getStretchPanelWidth(triggerWidth: number | null = null): number {
    const resolvedTriggerWidth = triggerWidth ?? this.getTriggerWidth() ?? 0;
    return Math.max(resolvedTriggerWidth, this.stretchMinWidth);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
